import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { X, BellOff, FileWarning, AlertTriangle, Clock, Wallet, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const KIND = {
  vouchers_missing: { icon: FileWarning, cls: 'text-rose-600 bg-rose-50' },
  overdue_invoices: { icon: AlertTriangle, cls: 'text-rose-600 bg-rose-50' },
  idle_leads: { icon: Clock, cls: 'text-amber-600 bg-amber-50' },
  payment_received: { icon: Wallet, cls: 'text-emerald-600 bg-emerald-50' },
};

/**
 * Mobile notifications bottom sheet — real data from /api/notifications.
 * Opening it marks actionable items read (clears the bell badge) and each
 * row deep-links to its action surface.
 */
export const MobileNotifications = ({ open, onClose, onRead }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/notifications`, { withCredentials: true })
      .then((r) => setItems(Array.isArray(r.data?.items) ? r.data.items : []))
      .catch(() => {})
      .finally(() => setLoading(false));
    axios.post(`${BACKEND_URL}/api/notifications/read-all`, {}, { withCredentials: true })
      .then(() => onRead?.())
      .catch(() => {});
    // onRead is a stable badge-clear callback; only re-run when the sheet opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const go = (href) => { onClose(); if (href) navigate(href); };

  return (
    <>
      <button type="button" aria-label="Close" onClick={onClose} className="fixed inset-0 z-[60] bg-black/30" data-testid="m-notif-backdrop" />
      <div className="fixed inset-x-0 bottom-0 z-[61] bg-white rounded-t-2xl border-t border-[#E5E7EB] flex flex-col" style={{ maxHeight: '78vh', paddingBottom: 'env(safe-area-inset-bottom)' }} data-testid="m-notif-sheet">
        <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b border-[#E5E7EB]">
          <p className="text-[16px] font-semibold text-[#111827]">Notifications</p>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="m-notif-close" className="w-9 h-9 flex items-center justify-center text-[#6B7280]"><X className="h-5 w-5" /></button>
        </div>

        <div className="overflow-y-auto px-2 py-2">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#0F6E56]" /></div>
          ) : items.length === 0 ? (
            <div className="text-center text-[14px] text-[#6B7280] py-12" data-testid="m-notif-empty">
              <BellOff className="h-7 w-7 mx-auto text-[#D1D5DB] mb-2" />
              You&apos;re all caught up.
            </div>
          ) : (
            items.map((n, i) => {
              const meta = KIND[n.kind] || KIND.idle_leads;
              const Icon = meta.icon;
              return (
                <button key={n.id || i} type="button" onClick={() => go(n.href)} data-testid={`m-notif-${i}`}
                  className="w-full flex items-start gap-3 px-3 py-3 rounded-xl text-left active:bg-[#F8F9FA]">
                  <span className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${meta.cls}`}><Icon className="h-[18px] w-[18px]" /></span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[14px] font-medium text-[#111827] truncate">{n.title}</span>
                    <span className="block text-[12px] text-[#6B7280] truncate">{n.subtitle}</span>
                  </span>
                </button>
              );
            })
          )}
        </div>
      </div>
    </>
  );
};

export default MobileNotifications;
